import type {
  ConfigField,
  ContextFieldKind,
  ContextFieldSpec,
  DashboardSnapshot,
  DashboardWindowDays,
  DecisionResponse,
  ProviderEntityView,
  ReasonCodeDescriptor,
  RuleCondition,
  SystemHealth,
} from "@sauron/contracts"

/**
 * The back office talks to the same HTTP API as every apiClient, under
 * `/v1/admin/…` with a session rather than an API key. Using the same API is
 * deliberate: it keeps that API honest instead of growing a private back door.
 *
 * **The self-describing shapes come from `@sauron/contracts`, not a copy.** The
 * panel renders what a source reports about itself — its health, its section on
 * the entity screen, its config fields — and those shapes are the API's
 * contract. Re-declaring them here left the client and server with no
 * compile-time link, so a field renamed in a contract left the panel silently
 * reading a stale shape. They are imported and re-exported below; a change to
 * the contract now breaks this build, which is the point.
 */
export type {
  ConfigField,
  DashboardDay,
  DashboardProvider,
  DashboardReason,
  DashboardSnapshot,
  DashboardTotals,
  DashboardWindowDays,
  HealthAction,
  HealthMetric,
  HealthStatus,
  HealthTable,
  ProviderEntityView,
  ProviderHealth,
  ProviderTraffic,
  ReasonCodeDescriptor,
  SystemHealth,
} from "@sauron/contracts"

/** The panel's name for the whole-system snapshot; it *is* the contract's `SystemHealth`. */
export type HealthSnapshot = SystemHealth

export interface Session {
  readonly email: string
  readonly name: string
}

export interface EntitySummary {
  readonly taxId: { readonly country: string; readonly value: string }
  readonly providers: readonly ProviderEntityView[]
  /**
   * Core-owned: facts about us, not about any register. Every override in
   * force, one per question at most — an override is scoped to one question.
   */
  readonly activeOverrides: readonly OverrideSummary[]
  readonly decisions: readonly DecisionSummary[]
}

/** One row of the Podmioty list: what the core owns, plus each source's one word. */
export interface EntityListItem {
  readonly country: string
  readonly taxId: string
  /** The registered name, from whichever source holds one. Null when none does. */
  readonly name: string | null
  readonly firstSeenAt: string
  readonly decisionCount: number
  readonly lastDecision: {
    readonly decisionId: string
    readonly question: string
    readonly decision: "ALLOW" | "PREPAYMENT" | "REVIEW"
    readonly degraded: boolean
    readonly evaluatedAt: string
  } | null
  readonly activeOverrides: number
  /** Of those, the ones with no expiry — in force until somebody revokes them. Marked on the row. */
  readonly openEndedOverrides: number
  readonly sources: readonly {
    readonly providerId: string
    readonly title: string
    readonly headline: NonNullable<ProviderEntityView["headline"]>
  }[]
}

export interface EntitySummaryFigures {
  readonly known: number
  readonly askedLast30Days: number
  readonly withActiveOverride: number
  readonly lastPrepayment: number
}

export interface DecisionSummary {
  readonly decisionId: string
  readonly apiClientId: string
  readonly country: string
  readonly taxId: string
  /** Which question this answered. Today always deferred payment. */
  readonly question: string
  readonly decision: "ALLOW" | "PREPAYMENT" | "REVIEW"
  readonly degraded: boolean
  readonly evaluatedAt: string
  readonly reasonCodes: readonly string[]
}

export interface DecisionDetail extends DecisionSummary {
  readonly configVersion: number
  readonly latencyMs: number
  /** What the rules reached, when a person substituted another decision. */
  readonly decisionBeforeOverride: "ALLOW" | "PREPAYMENT" | "REVIEW" | null
  readonly overrideId: string | null
  readonly reasons: readonly {
    /** Evaluation order, as recorded. The reason's identity, not its position. */
    readonly seq: number
    readonly code: string
    readonly rule: string | null
    readonly kind: string
    readonly observed: Record<string, unknown> | null
  }[]
  readonly providerCalls: readonly {
    readonly providerId: string
    readonly source: "local" | "cache" | "api" | "none"
    readonly outcome: string
    readonly latencyMs: number
    readonly quotaSpent: number
  }[]
}

export interface OverrideSummary {
  readonly overrideId: string
  readonly country: string
  readonly taxId: string
  readonly question: string
  readonly direction: "ALLOW" | "PREPAYMENT"
  readonly validFrom: string
  /** Null is open-ended: in force until revoked. */
  readonly validUntil: string | null
  readonly note: string
  readonly author: string
  readonly createdAt: string
  readonly revokedAt: string | null
  readonly revokedBy: string | null
}

/**
 * What the form sends. **No author**: the route takes it from the session, and
 * refuses a body that names one — an override attributed to whoever the caller
 * claims is not a record.
 */
export interface OverrideDraft {
  readonly taxId: string
  readonly question: string
  readonly direction: "ALLOW" | "PREPAYMENT"
  /** ISO, bounded by `Registry.overridePolicy.maxDurationMs` from now — or null for open-ended. */
  readonly validUntil: string | null
  readonly note: string
}

export interface ConfigVersionSummary {
  readonly version: number
  readonly author: string
  readonly note: string | null
  readonly createdAt: string
  readonly config: Record<string, unknown>
}

export interface Registry {
  /** Settings every rule has, declared once so the panel does not hardcode them. */
  readonly commonRuleFields: readonly ConfigField[]
  readonly engine: {
    readonly title: string
    readonly description: string
    readonly fields: readonly ConfigField[]
  }
  readonly rules: readonly {
    readonly ruleId: string
    readonly title: string
    readonly description: string
    readonly defaultMode: "veto" | "score"
    /** Absent means `veto`. Only an enrichment rule declares anything else. */
    readonly defaultOnUnavailable?: "veto" | "points" | "ignore"
    readonly dependsOn: readonly string[]
    /** The questions this rule answers. Offered and run only under those. */
    readonly questions: readonly string[]
    /** The codes it writes into the audit log — what a person carries into a ticket. */
    readonly reasonCodes?: readonly string[]
    readonly fields: readonly ConfigField[]
    /** Conditions a fresh configuration starts this rule with. Absent means none. */
    readonly defaultConditions?: readonly RuleCondition[]
  }[]
  readonly providers: readonly {
    readonly providerId: string
    readonly title: string
    readonly description: string
    readonly countries: readonly string[] | "all"
    /** 0 local, 100 unmetered, 200+ spends a quota or money. */
    readonly worstCaseCost: number
    readonly fields: readonly ConfigField[]
  }[]
  /**
   * Every question this build answers. Rule sets, decisions and overrides each
   * belong to one, and the panel names it by its Polish title.
   */
  /**
   * What a rule's context conditions are built from. Served rather than
   * hardcoded, so a field added to `context` for a second question reaches the
   * builder without an edit here.
   */
  readonly conditions: {
    readonly fields: readonly ContextFieldSpec[]
    readonly operators: Readonly<
      Record<ContextFieldKind, readonly { readonly value: string; readonly label: string }[]>
    >
    readonly missingPolicies: readonly { readonly value: string; readonly label: string }[]
  }
  readonly questions: readonly {
    readonly questionId: string
    readonly title: string
    readonly description: string
  }[]
  /**
   * Every code the installed packages can produce, assembled at startup from
   * what is installed. The filter reads this instead of asking somebody to
   * remember `mf.vat_removed`.
   */
  readonly reasonCodes: readonly ReasonCodeDescriptor[]
  /**
   * The bounds the override route refuses outside of. The form renders them —
   * the latest expiry it offers, the shortest note it accepts — from here, so
   * they cannot drift from what the API enforces.
   */
  readonly overridePolicy: {
    readonly maxDurationMs: number
    readonly minNoteLength: number
  }
}

export interface ApiClientSummary {
  readonly apiClientId: string
  readonly name: string
  readonly active: boolean
  /** Decisions this key has driven, all-time. */
  readonly decisionCount: number
  /** When the key was last used, ISO — null if it never has been. */
  readonly lastUsedAt: string | null
}

/** The plaintext key, returned exactly once at issue and never recoverable. */
export interface IssuedKey {
  readonly apiClientId: string
  readonly key: string
}

/**
 * One person's grant of access to the panel. Revocation is a fact on the row,
 * never a delete, so the list and its history are the same rows.
 */
export interface AccessGrant {
  readonly grantId: string
  readonly email: string
  readonly grantedBy: string
  readonly grantedAt: string
  readonly revokedAt: string | null
  readonly revokedBy: string | null
}

export interface AccessList {
  readonly items: AccessGrant[]
  /** The Workspace domains a person must be in regardless of the list. */
  readonly allowedDomains: string[]
}

export class ApiError extends Error {
  readonly status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = "ApiError"
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/v1/admin${path}`, {
    ...init,
    // Google Workspace SSO, so the browser carries the session cookie.
    credentials: "include",
    headers: { accept: "application/json", ...(init?.headers ?? {}) },
  })

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: string }
    throw new ApiError(response.status, body.message ?? response.statusText)
  }
  // A 204 (e.g. a deactivation) carries no body; calling .json() on it throws.
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const api = {
  session: () => request<Session>("/session"),
  /** Drops the session cookie. POST, so a stray link cannot sign someone out. */
  logout: () => request<void>("/auth/logout", { method: "POST" }),
  entity: (country: string, taxId: string) =>
    request<EntitySummary>(`/entities/${country}/${taxId}`),
  entities: (query: Record<string, string>, cursor?: string) =>
    request<{ items: EntityListItem[]; nextCursor: string | null }>(
      `/entities?${new URLSearchParams(cursor === undefined ? query : { ...query, cursor })}`,
    ),
  entitySummary: () => request<EntitySummaryFigures>("/entities/summary"),
  decisions: (query: Record<string, string>, cursor?: string) =>
    request<{ items: DecisionSummary[]; nextCursor: string | null }>(
      `/decisions?${new URLSearchParams(cursor === undefined ? query : { ...query, cursor })}`,
    ),
  decision: (decisionId: string) => request<DecisionDetail>(`/decisions/${decisionId}`),
  health: () => request<HealthSnapshot>("/health"),
  /** The Pulpit over a window of Warsaw days, today included. Read from the audit log alone. */
  dashboard: (days: DashboardWindowDays) => request<DashboardSnapshot>(`/dashboard?days=${days}`),
  /**
   * Runs an action a source declared on its health report — today only KRZ's
   * bootstrap. The value is a credential, so it travels in the body and never
   * in the path, and nothing here keeps it after the call returns.
   */
  runProviderAction: (providerId: string, actionKey: string, value: string) =>
    request<{ message: string }>(`/providers/${providerId}/actions/${actionKey}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ value }),
    }),
  configVersions: () => request<{ items: ConfigVersionSummary[] }>("/config"),
  registry: () => request<Registry>("/registry"),
  publishConfig: (config: unknown, note: string | null) =>
    request<{ version: number }>("/config", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ config, note }),
    }),
  overrides: () => request<{ items: OverrideSummary[] }>("/overrides"),
  /** Creates an override in the name of the signed-in identity. A refusal is a 400 with a sentence. */
  createOverride: (draft: OverrideDraft) =>
    request<OverrideSummary>("/overrides", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(draft),
    }),
  /**
   * Revokes an override, effective immediately and attributed to the signed-in
   * identity. Not a delete: the row stays, with who revoked it and when.
   */
  revokeOverride: (overrideId: string) =>
    request<void>(`/overrides/${overrideId}`, { method: "DELETE" }),
  apiClients: () => request<{ items: ApiClientSummary[] }>("/api-clients"),
  /**
   * Issues a new apiClient's key. The plaintext is in the response and nowhere
   * else, ever again — a lost key is rotated, not read back.
   */
  createApiClient: (input: { apiClientId: string; name: string }) =>
    request<IssuedKey>("/api-clients", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    }),
  /** Rotates a apiClient's key; the new plaintext is in the response, once. */
  rotateApiClientKey: (apiClientId: string) =>
    request<IssuedKey>(`/api-clients/${apiClientId}/rotate`, { method: "POST" }),
  /** Deactivates a apiClient; its key stops resolving immediately. */
  revokeApiClient: (apiClientId: string) =>
    request<void>(`/api-clients/${apiClientId}`, { method: "DELETE" }),
  /** Who may open the panel: every grant, in force or revoked, and the domain rule beside it. */
  access: () => request<AccessList>("/access"),
  /**
   * Grants one address access, in the name of the signed-in identity. A
   * refusal — not an address, outside the domain, already listed, or a first
   * entry that is not your own — is a 4xx with a sentence.
   */
  grantAccess: (email: string) =>
    request<AccessGrant>("/access", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email }),
    }),
  /** Revokes a grant, felt on that person's next request. The row stays, with who and when. */
  revokeAccess: (grantId: string) => request<void>(`/access/${grantId}`, { method: "DELETE" }),
  /**
   * Asking here is a **real decision** — recorded like any other, with a paid
   * check attributed to the back office. There is no unlogged, unpriced way to
   * ask. A cart value travels as an order's would, so the rules conditioned on
   * it run as they would at checkout; without one they do not run at all.
   */
  checkTaxId: (
    taxId: string,
    question: string,
    requestId: string,
    cartValue?: { readonly amount: number; readonly currency: string },
  ) =>
    request<DecisionResponse>("/check", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        taxId,
        question,
        requestId,
        ...(cartValue === undefined ? {} : { context: { cartValue } }),
      }),
    }),
}
